/* eslint-disable sonarjs/no-identical-functions */

import { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { Colors, MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Button } from "@paclife/plds.button";

import { AppContext } from "context/AppContext";
import Typography from "common/Typography/Typography";
import BaseModal from "common/Modal/BaseModal";
import TextField from "common/TextField/TextField";
import Message from "common/Message/Message";

import { PRIMARY_CEMAIL_TYPE, OTHER_CEMAIL_TYPE } from "constants/index";
import validator from "utils/validator";
import { updateCEmail, makePrimary, addCEmail } from "api/cemail";

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled(Typography)`
    color: ${Colors.primary};
    margin: ${Spacing[16]} 0 ${Spacing[24]};
    // tablet and mobile
    @media (max-width: ${MediaQueries.lg}) {
        margin: ${Spacing[8]} 0;
    }
`;

const SubTitle = styled(Typography)`
    margin-bottom: ${Spacing[16]};
`;

const CancelButton = styled(Button)`
    width: 108px;
`;
const ConfirmButton = styled(Button)`
    width: 108px;
`;

const EditModal = ({ closeModal, type, email: currentEmail }) => {
    const { t } = useTranslation("editModal");
    const { setCEmails, fetchCEmails, cEmails } = useContext(AppContext);

    const [email, setEmail] = useState("");
    const [verifyEmail, setVerifyEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const [formatError, setFormatError] = useState(false);
    const [verifyEmailError, setVerifyEmailError] = useState(false);
    const [updateError, setUpdateError] = useState("");
    const [updateDisabled, setUpdateDisabled] = useState(true);

    const handleChangeEmail = (e) => {
        setEmail(e.target.value);
    };

    const handleChangeVerifyEmail = (e) => {
        setVerifyEmail(e.target.value);
    }

    useEffect(() => {
        if (type === "swap") {
            setUpdateDisabled(false);
            return;
        }
        const valid = validator.Email(email);
        if (email === "" || valid) {
            setFormatError(false);
        } else {
            setFormatError(true);
        }

        if (verifyEmail === email) {
            setVerifyEmailError(false);
        } else {
            setVerifyEmailError(true);
        }

        if (email !== "" && valid && !verifyEmailError) {
            setUpdateDisabled(false);
        } else {
            setUpdateDisabled(true);
        }

        if (updateError !== "") {
            setUpdateError("");
        }
    }, [email, verifyEmail, verifyEmailError]);

    const update = async () => {
        if (type === PRIMARY_CEMAIL_TYPE || type === OTHER_CEMAIL_TYPE) {
            return await updateCEmail({
                oldStatusType: type,
                oldEmailAddress: currentEmail,
                statusType: type,
                emailAddress: email
            });
        }
        if (type === "swap") {
            return await makePrimary({ emailAddress: currentEmail });
        }
        if (type === "add") {
            return await addCEmail({
                statusType: OTHER_CEMAIL_TYPE,
                emailAddress: email
            });
        }
        throw Error(`Unhandled type ${type}.`);
    };

    const updateLocalState = () => {
        if (type === PRIMARY_CEMAIL_TYPE || type === OTHER_CEMAIL_TYPE) {
            setCEmails((prev) => {
                return prev.map((item) => {
                    if (
                        item.emailAddr.toLowerCase() ===
                        currentEmail.toLowerCase()
                    ) {
                        item.emailAddr = email;
                    }
                    return item;
                });
            });
            return;
        }
        if (type === "swap") {
            setCEmails((prev) => {
                const primaryEmail = prev.filter(
                    (el) => el.statusType === PRIMARY_CEMAIL_TYPE
                )[0].emailAddr;

                return prev.map((item) => {
                    if (
                        item.emailAddr.toLowerCase() ===
                        currentEmail.toLowerCase()
                    ) {
                        item.emailAddr = primaryEmail;
                    }
                    if (item.statusType === PRIMARY_CEMAIL_TYPE) {
                        item.emailAddr = currentEmail;
                    }
                    return item;
                });
            });
            return;
        }
        if (type === "add") {
            setCEmails((prev) => {
                const curr = [...prev];
                curr.push({
                    id: null,
                    statusType: OTHER_CEMAIL_TYPE,
                    emailAddr: email
                });
                return curr;
            });
            return;
        }
        throw Error(`Unhandled type ${type}.`);
    };

    const handleUpdate = async () => {
        // can't add email already in use
        if (cEmails.some(item => item.emailAddr === email)) { 
            setUpdateError(t(`editModal:add.error`)); 
            return;
        }
        setLoading(true);
        const { succeed, message } = await update();
        setLoading(false);
        if (succeed) {
            updateLocalState();
            fetchCEmails();
            closeModal();
        } else {
            setUpdateError(message);
        }

    };

    const handleCancel = async () => {
        setEmail("");
        closeModal();
    };

    return (
        <BaseModal
            size="default"
            secondaryButton={
                <CancelButton
                    variant="secondaryNeutral"
                    onClick={handleCancel}
                    data-track="analytics"
                    data-bname="close-edit-cemail-modal"
                >
                    {t(`${type}.button.secondary`)}
                </CancelButton>
            }
            primaryButton={
                <ConfirmButton
                    onClick={handleUpdate}
                    disabled={updateDisabled}
                    data-track="analytics"
                    data-bname={
                        type === "add" || type === "swap"
                            ? `${type}-cemail`
                            : `update-${type}-cemail`
                    }
                >
                    {t(`${type}.button.primary`)}
                </ConfirmButton>
            }
            closeModal={closeModal}
            loading={loading}
        >
            <Content>
                <Title variant="h2" display="block" align="center">
                    {t(`${type}.title`)}
                </Title>
                <SubTitle variant="body" align="center">
                    {t(`${type}.subtitle`, { value: currentEmail })}
                </SubTitle>

                {type !== "swap" && (
                    <>
                        <TextField
                            autoFocus
                            id="email"
                            name="email"
                            value={email}
                            onChange={handleChangeEmail}
                            error={formatError}
                            errorMessage="Please enter correct format"
                            placeholder="Enter your email address"
                        />
                        <TextField
                            id="verifyEmail"
                            name="verifyEmail"
                            value={verifyEmail}
                            onChange={handleChangeVerifyEmail}
                            allowpaste={false}
                            test='testing'
                            autocomplete="off"
                            placeholder="Confirm your email address"
                            error={verifyEmailError}
                            errorMessage="Email addresses do not match"
                            style={{ margin: "15px auto auto auto" }}
                        />
                    </>
                )}
                {updateError && <Message type="error" message={updateError} />}
            </Content>
        </BaseModal>
    );
};

export default EditModal;

EditModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    type: PropTypes.oneOf([PRIMARY_CEMAIL_TYPE, OTHER_CEMAIL_TYPE])
};